import {
  AddRounded,
  ConstructionOutlined,
  ContactsOutlined,
  CurrencyExchangeOutlined,
  FlagOutlined,
  GavelOutlined,
  GroupsOutlined,
  HistoryOutlined,
  HomeWorkOutlined,
  InfoOutlined,
  InsertDriveFileOutlined,
  KeyboardArrowRightRounded,
  PaymentsOutlined,
  PersonOutline,
  SavingsOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { Box, Container } from "@mui/material";
import { isNil, size } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { AppraisalStatusChip } from "../../../../components/appraisalOrders/appraisalOrderUi";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import {
  useDrilldownBaseUrl,
  useIsAcquisitions,
} from "../../../../pipelines/PipelineContext";
import UpbLeadCard from "../../shared/upb/UpbLeadCard";
import UpbOverTimeCard from "../../shared/upb/UpbOverTimeCard";
import { hasUpbChart } from "../../shared/upb/upbModel";
import UpbInfoBox, { hasBothUpbs } from "../../shared/upb/UpbInfoBox";
import { isConstructionProduct } from "../../../../utils/loan/productType";
import { sidebarTypeToggle } from "../../../../redux/actions/sidebarActions";
import {
  DETAIL_SIDEBAR_CONTENT_TYPES,
  detailSidebarOpen,
} from "../../../../redux/reducers/detailSidebarReducer";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import {
  actionPhrase,
  eventDocuments,
  metaFor,
  statusTransition,
  summaryLine,
} from "../../sidebar/types/LoanChangelog/loanChangelogConfig";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import { AssignedTeamRows, FONT_MONO, Hero } from "./summaryShared";
import {
  BentoColumn,
  BentoGrid,
  useInterestReserveRows,
  useLoanInformationOverviewRows,
  usePurchaseRows,
  useQuickAttributesRows,
  useRefiRows,
  useRehabInfoRows,
} from "./summaryBento";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
import { isPurchaseLikeLoan } from "../../../../utils/loans/delayedPurchase";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — ported verbatim from the target mockup.
   ═══════════════════════════════════════════════════════════ */
const C = {
  bg: "#f4f6f8",
  card: "#ffffff",
  ink: "#10151c",
  ink2: "#4a5560",
  ink3: "#8a96a3",
  line: "#e4e9f0",
  line2: "#eef1f4",
  accent: "#1f4e8c",
  accentSoft: "#eaf1fa",
  accentLine: "#dbe7f6",
  blue: "#1f4e8c",
  blueSoft: "#eaf1fa",
  blueLine: "#dbe7f6",
  amber: "#b4690e",
};
// Flat cards — no elevation, borders only (matches the reference mockup).
const SHADOW = "none";

/* date helpers — the mockup uses short month/day, e.g. "Mar 5" / "Apr 16" */
const shortDateYear = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return formatDate(v, "", "MMM d, yyyy");
};

const pctOrDash = (v) => (isNil(v) ? "—" : formatPercent(v));

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
const LoanDrilldownSummaryTab = () => {
  const loan = useSelector((state) => state.loanDrilldown);
  const navigate = useNavigate();
  // Pipeline-aware: an acquisition drilldown lives at /acquisition/:id, so a
  // hardcoded /loan/:id here lands on the preclose screen, which looks the deal
  // up in /underwriting/* and reports "loan not found".
  const baseUrl = useDrilldownBaseUrl(loan?._id);
  const isAcquisition = useIsAcquisitions();

  return (
    <LoanDrilldownTabPanel>
      <Box
        sx={{
          minHeight: "100%",
          background: `radial-gradient(1200px 400px at 80% -10%, rgba(20,84,58,.05), transparent 60%), ${C.bg}`,
          p: { xs: "20px 16px", md: "28px" },
        }}
      >
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "18px",
          }}
        >
          <LoanTerminalStatusBanner loan={loan} />
          <Hero loan={loan} />
          <AppraisalOrderBanner
            loan={loan}
            navigate={navigate}
            baseUrl={baseUrl}
          />
          <Metrics loan={loan} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" },
              gap: "18px",
              alignItems: "start",
            }}
          >
            <LoanInfoPanel loan={loan} navigate={navigate} baseUrl={baseUrl} />
            <AssignedTeamPanel loan={loan} />
            {/* Recent Activity card is built but hidden for now. To re-enable,
                stack it under Assigned Team in a right column:
                  <Flx column g={2.25} sx={{ minWidth: 0 }}>
                    <AssignedTeamPanel loan={loan} />
                    <RecentActivityPanel loan={loan} />
                  </Flx>
                RecentActivityPanel / ActivityRow are defined below. */}
          </Box>

          {/* Purchased loan: the balance being bought + its path to maturity. */}
          {/* Only when both UPBs are on the loan AND there is a chart to
              draw (a cut-off / settlement date to anchor it). */}
          {isAcquisition && hasUpbChart(loan) && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1fr 2fr" },
                gap: "18px",
                alignItems: "start",
              }}
            >
              <UpbLeadCard loan={loan} sx={{ borderRadius: "16px" }} />
              <UpbOverTimeCard
                loan={loan}
                sx={{
                  borderRadius: "16px",
                  border: `1px solid ${C.line}`,
                  boxShadow: SHADOW,
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" },
              gap: "18px",
              alignItems: "start",
            }}
          >
            <ApplicantsPanel
              loan={loan}
              navigate={navigate}
              baseUrl={baseUrl}
            />
            <PropertiesPanel
              loan={loan}
              navigate={navigate}
              baseUrl={baseUrl}
            />
            <ContactsPanel loan={loan} navigate={navigate} baseUrl={baseUrl} />
          </Box>

          <LoanBreakdownBento loan={loan} />
        </Container>
      </Box>
    </LoanDrilldownTabPanel>
  );
};

/* ─── Closed / Dead banner ─────────────────────────────────
   Terminal-status strip pinned above every card: green + gavel once the loan
   has closed, red + flag once it's dead (with the reason and any notes).
   Who/when come from the `milestoneStatusChanges` audit entry the backend
   appends on the status change (`updateBy` / `milestoneChangeDate`); we fall
   back to the dedicated date fields (`loanDeadDate` for dead, the closing date
   for closed) when the audit entry is missing on older loans. */
const CLOSED_STATUSES = ["Closed", "Closed w/ Post Closing Conditions"];

const LoanTerminalStatusBanner = ({ loan }) => {
  const users = useSelector((state) => state.users);

  const status = loan?.loanStatus;
  const isDead = status === "Dead";
  const isClosed = CLOSED_STATUSES.includes(status);
  if (!isDead && !isClosed) return null;

  // Latest milestone entry for the CURRENT status = the change that put the
  // loan here (a loan can be re-closed, so take the last match).
  const change = [...(loan?.milestoneStatusChanges || [])]
    .reverse()
    .find((c) => c?.updatedMilestoneStatus === status);

  const byEmail = change?.updateBy;
  const byName = byEmail
    ? getUserFullNameFromEmail(users, byEmail) || byEmail
    : null;

  const whenRaw = isDead
    ? loan?.loanDeadDate || change?.milestoneChangeDate
    : change?.milestoneChangeDate ||
      loan?.estimatedClosingDate ||
      loan?.originationDate;
  const when = whenRaw ? formatDateTime(whenRaw, "") : null;

  const tone = isDead
    ? {
        bg: "#fff5f5",
        border: "#f3c9c9",
        iconBg: "#fadcdc",
        fg: "#8f2323",
        sub: "#ab5a5a",
      }
    : {
        bg: "#f2fbf6",
        border: "#c4e7d3",
        iconBg: "#d7f2e3",
        fg: "#14603a",
        sub: "#4a8767",
      };

  const title = isDead
    ? "Loan marked dead"
    : status === "Closed w/ Post Closing Conditions"
      ? "Loan closed — post-closing conditions outstanding"
      : "Loan closed";

  const meta = [
    when ? `${isDead ? "Marked dead" : "Closed"} ${when}` : null,
    byName ? `by ${byName}` : null,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        px: "16px",
        py: "11px",
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: "10px",
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: tone.iconBg,
          color: tone.fg,
          flexShrink: 0,
        }}
      >
        {isDead ? (
          <FlagOutlined sx={{ fontSize: 17 }} />
        ) : (
          <GavelOutlined sx={{ fontSize: 17 }} />
        )}
      </Box>

      <Flx column g={0.4} sx={{ flex: 1, minWidth: 0, pt: "2px" }}>
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: tone.fg }}>
          {title}
        </Txt>
        {meta.length > 0 && (
          <Txt sx={{ fontSize: 11.5, color: tone.sub }}>{meta.join(" · ")}</Txt>
        )}
        {isDead && loan?.dealDeadReason && (
          <Txt sx={{ fontSize: 12.5, color: tone.fg, mt: "2px" }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              Reason:{" "}
            </Box>
            {loan.dealDeadReason}
          </Txt>
        )}
        {isDead && loan?.dealDeadNotes && (
          <Txt
            sx={{
              fontSize: 12,
              color: tone.sub,
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
            }}
          >
            {loan.dealDeadNotes}
          </Txt>
        )}
      </Flx>
    </Box>
  );
};

/* ─── Appraisal order banner ───────────────────────────────
   Subtle strip below the hero noting an internal appraisal review has been
   sent out, with the latest status and a jump to the Orders tab. Hidden when
   there are no active (non-cancelled) orders. */
const AppraisalOrderBanner = ({ loan, navigate, baseUrl }) => {
  const orders = (loan?.appraisalOrders || []).filter(
    (o) => o?.status !== "cancelled",
  );
  if (orders.length === 0) return null;
  const latest = orders[0]; // backend returns newest-first
  const due = shortDateYear(latest?.requestedByDate);

  return (
    <Box
      onClick={() => navigate(`${baseUrl}/orders`)}
      role="button"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: "16px",
        py: "11px",
        background: "#fffaf2",
        border: "1px solid #f0e0c8",
        borderRadius: "10px",
        cursor: "pointer",
        transition: "background .15s, border-color .15s",
        "&:hover": { background: "#fdf3e3", borderColor: "#e7cfa8" },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fbe8d2",
          color: C.amber,
          flexShrink: 0,
        }}
      >
        <GavelOutlined sx={{ fontSize: 17 }} />
      </Box>

      <Flx column g={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Txt sx={{ fontSize: 13, fontWeight: 600, color: "#7a4f12" }}>
          Internal appraisal review
          {orders.length > 1 ? ` (${orders.length})` : ""} ordered
        </Txt>
        <Txt sx={{ fontSize: 11.5, color: "#9a7330" }}>
          Sent to the internal appraisers
          {due ? ` · needed by ${due}` : ""}
        </Txt>
      </Flx>

      <AppraisalStatusChip status={latest?.status} />
      <Txt
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          color: C.amber,
          whiteSpace: "nowrap",
        }}
      >
        View in Orders →
      </Txt>
    </Box>
  );
};

/* ─── Loan breakdown bento ─────────────────────────────────
   Data comes from Summary Redesign 2's row-builder hooks, but rendered in
   THIS tab's native Panel/PanelHead/InfoRow chrome so the cards match the
   Loan Information / Assigned Team cards. Computed rows show the Σ marker
   (InfoRow "sigma"); everything else is "plain". */
const FieldsPanel = ({ icon, title, rows }) => (
  <Panel>
    <PanelHead icon={icon} title={title} />
    <Box sx={{ p: "8px 20px 16px" }}>
      {rows.map((r) => (
        <InfoRow
          key={r.label}
          k={r.label}
          v={r.value || "—"}
          type={r.computed ? "sigma" : "plain"}
        />
      ))}
    </Box>
  </Panel>
);

const QuickAttributesCardN = ({ loan }) => (
  <FieldsPanel
    icon={<TuneOutlined />}
    title="Quick attributes"
    rows={useQuickAttributesRows(loan)}
  />
);

const InterestReserveCardN = ({ loan }) => (
  <FieldsPanel
    icon={<SavingsOutlined />}
    title="Interest (payment) reserve"
    rows={useInterestReserveRows(loan)}
  />
);

const LoanInformationOverviewCardN = ({ loan }) => (
  <FieldsPanel
    icon={<InfoOutlined />}
    title="Loan information overview"
    rows={useLoanInformationOverviewRows(loan)}
  />
);

const PurchaseCardN = ({ loan }) => (
  <FieldsPanel
    icon={<PaymentsOutlined />}
    title="Purchase"
    rows={usePurchaseRows(loan)}
  />
);

const RefiCardN = ({ loan }) => (
  <FieldsPanel
    icon={<CurrencyExchangeOutlined />}
    title="Refinance"
    rows={useRefiRows(loan)}
  />
);

const RehabInfoCardN = ({ loan }) => (
  <FieldsPanel
    icon={<ConstructionOutlined />}
    title="Rehab information"
    rows={useRehabInfoRows(loan)}
  />
);

const LoanBreakdownBento = ({ loan }) => {
  const purpose = loan?.loanPurpose;
  const productType = loan?.loanProductType;
  // A delayed purchase (cash-out + flag, or the legacy purpose string) gets
  // the Purchase card, as the retired "Delayed Purchase" purpose did.
  const isPurchase = isPurchaseLikeLoan(loan);
  const isRefinance =
    ["Refinance - No Cash Out", "Refinance - Cash Out"].includes(purpose) &&
    !isPurchase;
  const isFlipOrGroundUp = ["Fix and Flip", "Ground Up Construction"].includes(
    productType,
  );
  // Construction economics (interest reserve, rehab) are hidden for DSCR.
  const construction = isConstructionProduct(productType);

  const sideColWidth = 320;
  const mainColMinWidth = 440;

  return (
    <BentoGrid>
      {/* Left column holds the Interest Reserve card — construction-only. For
          DSCR the whole column is dropped so the overview + purchase columns
          flex-grow to fill the row (no empty gutter). */}
      {construction && (
        <BentoColumn
          flex={`1 1 ${sideColWidth}px`}
          minWidth={sideColWidth - 20}
        >
          {/* TEMP: Quick attributes card hidden — uncomment to restore. */}
          {/* <QuickAttributesCardN loan={loan} /> */}
          <InterestReserveCardN loan={loan} />
        </BentoColumn>
      )}

      <BentoColumn
        flex={`2 1 ${mainColMinWidth}px`}
        minWidth={mainColMinWidth - 20}
      >
        <LoanInformationOverviewCardN loan={loan} />
      </BentoColumn>

      <BentoColumn flex={`1 1 ${sideColWidth}px`} minWidth={sideColWidth - 20}>
        {isPurchase && <PurchaseCardN loan={loan} />}
        {isRefinance && <RefiCardN loan={loan} />}
        {!isPurchase && !isRefinance && <PurchaseCardN loan={loan} />}
        {isFlipOrGroundUp && <RehabInfoCardN loan={loan} />}
      </BentoColumn>
    </BentoGrid>
  );
};

/* ─── Metric cards ───────────────────────────────────────── */
const Metrics = ({ loan }) => {
  // ARV / LTARV are construction economics — hidden for DSCR.
  const construction = isConstructionProduct(loan?.loanProductType);
  const costToArv = useMemo(() => {
    const cost = Number(loan?.totalCostBasis);
    const arv = Number(loan?.totalArv);
    if (!Number.isFinite(cost) || !Number.isFinite(arv) || arv <= 0)
      return null;
    return `Cost to ARV ${formatPercent(cost / arv)}`;
  }, [loan?.totalCostBasis, loan?.totalArv]);

  const arvDelta = useMemo(() => {
    const arv = Number(loan?.totalArv);
    const asIs = Number(loan?.totalAsIsValue);
    if (!Number.isFinite(arv) || !Number.isFinite(asIs) || asIs <= 0)
      return null;
    const pct = Math.round(((arv - asIs) / asIs) * 100);
    return `${pct > 0 ? "+" : ""}${pct}% as-is`;
  }, [loan?.totalArv, loan?.totalAsIsValue]);

  const cards = [
    {
      label: "Total Loan Amount",
      value: formatDollar(loan?.totalLoanAmount, "—"),
      sub: isNil(loan?.baseLoanToAsIsValue)
        ? "Base"
        : `Base · LTV ${formatPercent(loan?.baseLoanToAsIsValue)}`,
      lead: true,
    },
    {
      label: "Total Purchase Price",
      value: formatDollar(loan?.totalPurchasePrice, "—"),
      sub: costToArv,
    },
    // ARV / LTARV cards are construction-only — omitted for DSCR.
    ...(construction
      ? [
          {
            label: "Total ARV",
            value: formatDollar(loan?.totalArv, "—"),
            sub: arvDelta,
          },
          {
            label: "Total LTARV",
            value: isNil(loan?.totalLoanToArv)
              ? "—"
              : formatPercent(loan?.totalLoanToArv),
            sub: isNil(loan?.totalHoldback)
              ? null
              : `Holdback ${formatDollar(loan?.totalHoldback)}`,
          },
        ]
      : []),
    {
      label: "Interest Rate",
      value: isNil(loan?.interestRate)
        ? "—"
        : formatPercent(loan?.interestRate),
      sub: loan?.interestCalcType || null,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: `repeat(${cards.length}, 1fr)`,
        },
        gap: "14px",
      }}
    >
      {cards.map((c) => (
        <Box
          key={c.label}
          sx={{
            background: c.lead
              ? "linear-gradient(155deg,#eef7f1,#ffffff 70%)"
              : C.card,
            border: `1px solid ${c.lead ? C.accentLine : C.line}`,
            borderRadius: "12px",
            boxShadow: SHADOW,
            p: "18px 18px 16px",
          }}
        >
          <Txt
            sx={{
              fontSize: 10.5,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              color: C.ink3,
              fontWeight: 600,
            }}
          >
            {c.label}
          </Txt>
          <Txt
            sx={{
              fontFamily: FONT_MONO,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              mt: "8px",
              color: C.ink,
            }}
          >
            {c.value}
          </Txt>
          {c.sub && (
            <Txt
              sx={{
                fontFamily: FONT_MONO,
                fontSize: 11,
                color: C.ink3,
                mt: "4px",
              }}
            >
              {c.sub}
            </Txt>
          )}
        </Box>
      ))}
    </Box>
  );
};

/* ─── Panel shell + header ───────────────────────────────── */
const Panel = ({ children, sx }) => (
  <Box
    sx={{
      background: C.card,
      border: `1px solid ${C.line}`,
      borderRadius: "12px",
      boxShadow: SHADOW,
      overflow: "hidden",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const PanelHead = ({ icon, title, count, endContent }) => (
  <Flx ac jb sx={{ p: "16px 20px", borderBottom: `1px solid ${C.line2}` }}>
    <Flx ac g={1}>
      <Flx ac sx={{ color: C.ink3, ".MuiSvgIcon-root": { fontSize: "16px" } }}>
        {icon}
      </Flx>
      <Txt
        sx={{
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: C.ink,
        }}
      >
        {title}
      </Txt>
      {count != null && <Count>{count}</Count>}
    </Flx>
    {endContent}
  </Flx>
);

const Count = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT_MONO,
      fontSize: 11,
      background: C.bg,
      border: `1px solid ${C.line}`,
      borderRadius: "6px",
      px: "7px",
      py: "1px",
      color: C.ink2,
    }}
  >
    {children}
  </Box>
);

const PanelLink = ({ children, onClick }) => (
  <Box
    component="span"
    onClick={onClick}
    sx={{
      fontSize: 11,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: C.accent,
      fontWeight: 600,
      cursor: "pointer",
      "&:hover": { opacity: 0.7 },
    }}
  >
    {children}
  </Box>
);

/* ─── Loan information panel ─────────────────────────────── */
const LoanInfoPanel = ({ loan, navigate, baseUrl }) => {
  const isAcquisition = useIsAcquisitions();
  // Only when the loan carries both UPBs — no box of dashes.
  const showUpb = isAcquisition && hasBothUpbs(loan);
  // Holdback / borrower class / LTC / exit strategy are construction-only or
  // otherwise not shown for DSCR.
  const construction = isConstructionProduct(loan?.loanProductType);
  const rows = useMemo(
    () => [
      ...(construction
        ? [
            {
              k: "Total Holdback",
              v: isNil(loan?.totalHoldback)
                ? null
                : formatDollar(loan?.totalHoldback),
              type: "chip",
            },
          ]
        : []),
      { k: "Loan Product", v: loan?.loanProductType, type: "chip" },
      { k: "Interest Calc", v: loan?.interestCalcType, type: "chip" },
      { k: "Loan Purpose", v: loan?.loanPurpose, type: "chip" },
      ...(construction
        ? [
            {
              // Effective borrower classification: the UW override when set
              // (flagged), otherwise the system-calculated value. Read-only
              // here — the override is edited in Loan Info. Not shown for DSCR.
              k: "Borrower Class.",
              v: loan?.borrower_classification_override
                ? `${loan.borrower_classification_override} (override)`
                : loan?.borrower_classification_calculated || "—",
              type:
                loan?.borrower_classification_override ||
                loan?.borrower_classification_calculated
                  ? "chip"
                  : "plain",
            },
          ]
        : []),
      // Acquisitions show Expected Settlement (when our money moves), same as
      // the header; the seller's Expected Closing stays on Loan Info only.
      isAcquisition
        ? {
            k: "Expected Settlement",
            v: shortDateYear(loan?.expectedSettlementDate) || "—",
            type: "plain",
          }
        : {
            k: "Expected Closing",
            v: shortDateYear(loan?.expectedClosingDate) || "—",
            type: "plain",
          },
      {
        k: "Base Loan to As-Is",
        v: pctOrDash(loan?.baseLoanToAsIsValue),
        type: "sigma",
      },
      ...(construction
        ? [
            {
              k: "Base Loan to Cost",
              v: pctOrDash(loan?.baseLoanToCost),
              type: "sigma",
            },
            { k: "Exit Strategy", v: loan?.exitStrategy || "—", type: "plain" },
          ]
        : []),
      {
        k: "Base Loan to Purchase",
        v: pctOrDash(loan?.baseLoanToPurchasePrice),
        type: "sigma",
      },
    ],
    [loan, construction, isAcquisition],
  );

  return (
    <Panel>
      <PanelHead
        icon={<InfoOutlined />}
        title="Loan Information"
        endContent={
          <PanelLink onClick={() => navigate(`${baseUrl}/loan-information`)}>
            Edit Details →
          </PanelLink>
        }
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: "32px",
          p: "8px 20px 16px",
        }}
      >
        {/* Two explicit columns (row i → column i % 2, the order the grid
            used to flow them in) so the UPB box can sit at the top of the
            column that holds Loan Product. */}
        {[0, 1].map((col) => {
          const colRows = rows.filter((_, i) => i % 2 === col);
          const withUpb =
            showUpb && colRows.some((r) => r.k === "Loan Product");
          return (
            <Box key={col} sx={{ minWidth: 0 }}>
              {withUpb && (
                <UpbInfoBox
                  loan={loan}
                  onOpen={() => navigate(`${baseUrl}/economics`)}
                />
              )}
              {colRows.map((r) => (
                <InfoRow key={r.k} {...r} />
              ))}
            </Box>
          );
        })}
      </Box>
    </Panel>
  );
};

const InfoRow = ({ k, v, type }) => (
  <Flx ac jb g={1.5} sx={{ py: "10px", borderBottom: `1px solid ${C.line2}` }}>
    <Txt
      sx={{
        fontSize: 11,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: C.ink3,
        fontWeight: 500,
      }}
    >
      {k}
    </Txt>
    {type === "chip" ? (
      <Box
        component="span"
        sx={{
          background: C.blueSoft,
          border: `1px solid ${C.blueLine}`,
          color: C.blue,
          fontFamily: FONT_MONO,
          fontSize: 11.5,
          fontWeight: 500,
          px: "9px",
          py: "2px",
          borderRadius: "7px",
        }}
      >
        {v || "—"}
      </Box>
    ) : (
      <Flx
        ac
        g={0.6}
        sx={{ fontFamily: FONT_MONO, fontSize: 12.5, fontWeight: 500 }}
      >
        {type === "sigma" && (
          <Box component="span" sx={{ color: C.accent, fontSize: 11 }}>
            Σ
          </Box>
        )}
        <span>{v}</span>
      </Flx>
    )}
  </Flx>
);

/* ─── Assigned team panel ─────────────────────────────────
   Body reuses the live summary card's AssignedTeamRows (exported from
   LoanDrilldownSummaryRedesignAltTab) so this matches the live look + inline
   edit behavior exactly. Only the Redesign-3 Panel/PanelHead shell is local. */
const ASSIGNEE_FIELDS = [
  "salesperson",
  "salesAnalyst",
  "processor",
  "underwriter",
  "loanCloser",
];

const AssignedTeamPanel = ({ loan }) => {
  const assignedCount = ASSIGNEE_FIELDS.filter((f) => loan?.[f]).length;
  return (
    <Panel sx={{ overflow: "unset" }}>
      <PanelHead
        icon={<GroupsOutlined />}
        title="Assigned Team"
        count={assignedCount}
      />
      <Box sx={{ p: "6px 12px 10px" }}>
        <AssignedTeamRows loan={loan} />
      </Box>
    </Panel>
  );
};

/* ─── Recent activity ──────────────────────────────────────
   A compact, read-only digest of the loan's structured audit feed
   (GET /underwriting/loan_events/{dealId}) — the newest few changes, each row
   mirroring the full Activity Log sidebar. "View All" / "Show all" open that
   sidebar (LOAN_CHANGE_HISTORY). Reuses the changelog config's display helpers
   (metaFor / actionPhrase / summaryLine / statusTransition / eventDocuments) so
   wording + icons match the sidebar exactly. */
const lcFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const isViewEvent = (ev) =>
  ev?.event_type === "loan_view" || ev?.sub_event === "view_file";

const RecentActivityPanel = ({ loan }) => {
  const dispatch = useDispatch();
  const dealId = loan?._id;
  const { getLoanEvents } = useUnderwritingHook();
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isNil(dealId)) return undefined;
    let active = true;
    setLoading(true);
    setEvents(null);
    getLoanEvents({
      dealId,
      onSuccessFn: (data) => active && setEvents(data?.events || []),
      onCompleteFn: () => active && setLoading(false),
      onFailFn: () => active && setEvents([]),
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  // Changes only (view noise dropped); the feed already arrives newest-first.
  const changes = useMemo(
    () => (events || []).filter((ev) => !isViewEvent(ev)),
    [events],
  );
  const recent = changes.slice(0, 5);

  const todayCount = useMemo(() => {
    if (!changes.length) return 0;
    const today = formatDateTime(new Date().toISOString(), "yyyy-MM-dd");
    return changes.filter(
      (ev) => formatDateTime(ev.timestamp, "yyyy-MM-dd") === today,
    ).length;
  }, [changes]);

  const openLog = () =>
    dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.LOAN_CHANGE_HISTORY));

  return (
    <Panel>
      <PanelHead
        icon={<HistoryOutlined />}
        title="Recent Activity"
        count={
          todayCount > 0 ? `${todayCount} today` : changes.length || undefined
        }
        endContent={
          changes.length > 0 && (
            <PanelLink onClick={openLog}>View All →</PanelLink>
          )
        }
      />
      {loading && !events ? (
        <Box sx={{ p: "16px 20px" }}>
          <Txt sx={{ fontSize: 12.5, color: C.ink3 }}>Loading activity…</Txt>
        </Box>
      ) : recent.length === 0 ? (
        <Box sx={{ p: "16px 20px" }}>
          <Txt sx={{ fontSize: 12.5, color: C.ink3, fontStyle: "italic" }}>
            No recent activity
          </Txt>
        </Box>
      ) : (
        <Box>
          {recent.map((ev, i) => (
            <ActivityRow
              key={`${ev.timestamp}-${i}`}
              ev={ev}
              isLast={i === recent.length - 1}
            />
          ))}
        </Box>
      )}
      {changes.length > recent.length && (
        <Box
          onClick={openLog}
          sx={{
            p: "11px",
            textAlign: "center",
            borderTop: `1px solid ${C.line2}`,
            fontSize: 12,
            fontWeight: 600,
            color: C.accent,
            cursor: "pointer",
            transition: "background 0.15s",
            "&:hover": { background: C.bg },
          }}
        >
          Show all {changes.length} changes
        </Box>
      )}
    </Panel>
  );
};

// One compact activity row: category icon, actor + action sentence, a status
// pill (for stage transitions) or one-line summary, timestamp, and — for doc
// uploads — an expandable file list.
const ActivityRow = ({ ev, isLast }) => {
  const [open, setOpen] = useState(false);
  const m = metaFor(ev.event_type);
  const Icon = m.Icon;
  const transition = statusTransition(ev);
  const docs = eventDocuments(ev);
  const actor = ev.changed_by_name || ev.changed_by || "System";
  const expandable = docs.length > 0;

  // Doc uploads read best with a count ("uploaded 2 documents"); everything else
  // uses the shared action phrase lower-cased so it flows after the actor.
  const isUpload = /^upload/i.test(ev.sub_event || "") || docs.length > 0;
  const action =
    isUpload && docs.length
      ? `uploaded ${docs.length} document${docs.length === 1 ? "" : "s"}`
      : lcFirst(actionPhrase(ev));

  const sub = transition ? null : summaryLine(ev);

  return (
    <Flx
      g={1.25}
      onClick={expandable ? () => setOpen((o) => !o) : undefined}
      sx={{
        p: "11px 20px",
        alignItems: "flex-start",
        borderBottom: isLast ? "none" : `1px solid ${C.line2}`,
        cursor: expandable ? "pointer" : "default",
        transition: "background 0.15s",
        "&:hover": expandable ? { background: C.bg } : undefined,
      }}
    >
      <Flx
        center
        sx={{
          width: 28,
          height: 28,
          borderRadius: "8px",
          bgcolor: m.tint,
          color: m.accent,
          flexShrink: 0,
          mt: "1px",
          ".MuiSvgIcon-root": { fontSize: "15px" },
        }}
      >
        <Icon />
      </Flx>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Txt sx={{ fontSize: 13, color: C.ink2, lineHeight: 1.4 }}>
          <Box
            component="span"
            sx={{ fontFamily: FONT_MONO, fontSize: 12, color: C.ink3 }}
          >
            {actor}
          </Box>{" "}
          <Box component="span" sx={{ fontWeight: 600, color: C.ink }}>
            {action}
          </Box>
        </Txt>

        {transition ? (
          <Box sx={{ mt: "4px" }}>
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                py: "2px",
                px: "8px",
                borderRadius: "9px",
                color: m.accent,
                bgcolor: m.tint,
              }}
            >
              {transition.to}
            </Box>
          </Box>
        ) : (
          sub && (
            <Txt
              sx={{
                fontSize: 12,
                color: C.ink3,
                mt: "2px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {sub}
            </Txt>
          )
        )}

        {expandable && open && (
          <Box
            sx={{ mt: "8px", borderLeft: `2px solid ${C.line}`, pl: "10px" }}
          >
            {docs.map((d) => {
              const meta = [d.docGroup, d.docType].filter(Boolean).join(" · ");
              return (
                <Flx key={d.key} ac g={0.75} sx={{ py: "3px" }}>
                  <InsertDriveFileOutlined
                    sx={{ fontSize: 13, color: C.ink3, flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Txt
                      sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: C.ink,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {d.file_display_name}
                    </Txt>
                    {meta && (
                      <Txt sx={{ fontSize: 11, color: C.ink3 }}>{meta}</Txt>
                    )}
                  </Box>
                </Flx>
              );
            })}
          </Box>
        )}
      </Box>

      <Flx column sx={{ flexShrink: 0, alignItems: "flex-end" }}>
        <Txt
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 11,
            color: C.ink3,
            whiteSpace: "nowrap",
          }}
        >
          {formatDateTime(ev.timestamp, "HH:mm")}
        </Txt>
        {expandable && (
          <KeyboardArrowRightRounded
            sx={{
              fontSize: 15,
              color: C.ink3,
              mt: "2px",
              transition: "transform 0.18s",
              transform: open ? "rotate(90deg)" : "none",
            }}
          />
        )}
      </Flx>
    </Flx>
  );
};

/* ─── Entity panels (Applicants / Properties / Contacts) ─── */
const EntityRow = ({ icon, name, sub, badge, onClick }) => (
  <Flx
    ac
    g={1.5}
    onClick={onClick}
    sx={{
      p: "13px 18px",
      borderBottom: `1px solid ${C.line2}`,
      cursor: onClick ? "pointer" : "default",
      transition: "background 0.15s",
      "&:hover": { background: C.bg },
    }}
  >
    <Flx
      center
      sx={{
        width: 32,
        height: 32,
        borderRadius: "9px",
        background: C.bg,
        border: `1px solid ${C.line}`,
        color: C.ink3,
        flexShrink: 0,
        ".MuiSvgIcon-root": { fontSize: "15px" },
      }}
    >
      {icon}
    </Flx>
    <Box sx={{ minWidth: 0 }}>
      <Txt
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: C.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </Txt>
      {sub && (
        <Txt
          sx={{
            fontSize: 11.5,
            color: C.ink3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sub}
        </Txt>
      )}
    </Box>
    {badge && (
      <Box
        component="span"
        sx={{
          ml: "auto",
          fontFamily: FONT_MONO,
          fontSize: 9.5,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          background: C.bg,
          border: `1px solid ${C.line}`,
          color: C.ink2,
          px: "7px",
          py: "3px",
          borderRadius: "6px",
          flexShrink: 0,
        }}
      >
        {badge}
      </Box>
    )}
  </Flx>
);

// Empty-state row. Matches EntityRow's height (32px icon + 13px*2 padding =
// 58px) so an empty panel reads the same as a populated one.
const EmptyRow = ({ label }) => (
  <Flx
    ac
    sx={{
      p: "13px 18px",
      minHeight: 58,
      borderBottom: `1px solid ${C.line2}`,
    }}
  >
    <Txt sx={{ fontSize: 13, color: C.ink3, fontStyle: "italic" }}>{label}</Txt>
  </Flx>
);

const AddRow = ({ label, onClick }) => (
  <Flx
    center
    g={1}
    onClick={onClick}
    sx={{
      p: "13px 18px",
      fontSize: 12,
      color: C.accent,
      fontWeight: 500,
      cursor: "pointer",
      background: "#fbfdfc",
      transition: "background 0.15s",
      // Light teal hover tint (brand #1F5AF2) to match the site color scheme.
      "&:hover": { background: "rgba(31, 90, 242,0.10)" },
      ".MuiSvgIcon-root": { fontSize: "14px" },
    }}
  >
    <AddRounded />
    {label}
  </Flx>
);

const ApplicantsPanel = ({ loan, navigate, baseUrl }) => {
  const dispatch = useDispatch();
  const borrowers = loan?.borrowers || [];
  return (
    <Panel>
      <PanelHead
        icon={<PersonOutline />}
        title="Applicants"
        count={size(borrowers)}
        endContent={
          <PanelLink onClick={() => navigate(`${baseUrl}/applicants`)}>
            View All →
          </PanelLink>
        }
      />
      {borrowers.map((b) => {
        const role = b?.isGuarantor ? "Guarantor" : "Primary";
        return (
          <EntityRow
            key={b._id}
            icon={<PersonOutline />}
            name={
              `${b?.firstName || ""} ${b?.lastName || ""}`.trim() ||
              "Unnamed borrower"
            }
            sub={[role, b?.ficoMid ? `${b.ficoMid} FICO` : null]
              .filter(Boolean)
              .join(" · ")}
            badge={role}
            onClick={() =>
              dispatch(
                detailSidebarOpen(
                  DETAIL_SIDEBAR_CONTENT_TYPES.BORROWER,
                  b,
                  b?._id,
                  {
                    title:
                      `${b?.firstName || ""} ${b?.lastName || ""}`.trim() ||
                      "Borrower",
                    mode: "view",
                    source: "LoanDrilldownSummaryTab",
                    sourceField: "borrowers",
                  },
                ),
              )
            }
          />
        );
      })}
      {borrowers.length === 0 && <EmptyRow label="No applicants added" />}
      <AddRow
        label="Add borrower"
        onClick={() =>
          dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_BORROWER))
        }
      />
    </Panel>
  );
};

const PropertiesPanel = ({ loan, navigate, baseUrl }) => {
  const dispatch = useDispatch();
  const properties = loan?.subjectProperties || [];
  return (
    <Panel>
      <PanelHead
        icon={<HomeWorkOutlined />}
        title="Properties"
        count={size(properties)}
        endContent={
          <PanelLink onClick={() => navigate(`${baseUrl}/properties`)}>
            View All →
          </PanelLink>
        }
      />
      {properties.map((p) => {
        const cityState = [p?.address?.city, p?.address?.state]
          .filter(Boolean)
          .join(", ");
        return (
          <EntityRow
            key={p._id}
            icon={<HomeWorkOutlined />}
            name={addressLabel(p?.address) || "Unnamed property"}
            sub={[cityState, p?.propertyType].filter(Boolean).join(" · ")}
            badge="Subject"
            onClick={() =>
              dispatch(
                detailSidebarOpen(
                  DETAIL_SIDEBAR_CONTENT_TYPES.SUBJECT_PROPERTY,
                  p,
                  p?._id,
                  {
                    title: addressLabel(p?.address) || "Property",
                    mode: "view",
                    source: "LoanDrilldownSummaryTab",
                    sourceField: "subjectProperties",
                  },
                ),
              )
            }
          />
        );
      })}
      {properties.length === 0 && <EmptyRow label="No properties added" />}
      <AddRow
        label="Add property"
        onClick={() =>
          dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.ADD_PROPERTY))
        }
      />
    </Panel>
  );
};

const ContactsPanel = ({ loan, navigate, baseUrl }) => {
  const dispatch = useDispatch();
  const contacts = loan?.loanRelatedContacts || [];
  return (
    <Panel>
      <PanelHead
        icon={<ContactsOutlined />}
        title="Contacts"
        count={size(contacts)}
        endContent={
          <PanelLink
            onClick={() => navigate(`${baseUrl}/loan-related-contacts`)}
          >
            View All →
          </PanelLink>
        }
      />
      {contacts.map((c) => (
        <EntityRow
          key={c._id}
          icon={<PersonOutline />}
          name={
            `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
            "Unnamed contact"
          }
          sub={c?.company || c?.emailAddress || null}
          badge={c?.loanRole || "Contact"}
          onClick={() =>
            dispatch(
              detailSidebarOpen(
                DETAIL_SIDEBAR_CONTENT_TYPES.LOAN_RELATED_CONTACT,
                c,
                c?._id,
                {
                  title:
                    `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
                    "Contact",
                  mode: "view",
                  source: "LoanDrilldownSummaryTab",
                  sourceField: "loanRelatedContacts",
                },
              ),
            )
          }
        />
      ))}
      {contacts.length === 0 && <EmptyRow label="No contacts added" />}
      <AddRow
        label="Add contact"
        onClick={() =>
          dispatch(
            sidebarTypeToggle(
              loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT,
            ),
          )
        }
      />
    </Panel>
  );
};

export default LoanDrilldownSummaryTab;
